/* Pump finder hero — drumpump.com
 * The homepage card: Find My Pump's first question as nine links, and a chemical search that hands over to
 * Shop by Chemical. Tiles, chips and the search form are plain HTML from the section, so they work without this file.
 * This file turns the search into a dropdown of every chemical A–Z that narrows as you type (the same list, ranking and
 * flame marks as the tools: pump-engine.js and pump-chemicals.json, both loaded on first focus), with keyboard support
 * and analytics. No safety rule lives here: the tools decide what is shown, this card only carries the customer to them.
 */
(function () {
  'use strict';
  var inNode = typeof module !== 'undefined' && module.exports;
  var PE = inNode ? require('./pump-engine.js') : (window.PumpEngine || null);
  var H = {};

  function arr(v) { return Array.isArray(v) ? v : []; }

  // ---------------------------------------------------------------- pure helpers (tested in node)
  // base + params, skipping empty values; keeps any query the base already has
  H.withParams = function (base, params) {
    var q = Object.keys(params || {})
      .filter(function (k) { return params[k] !== undefined && params[k] !== null && String(params[k]) !== ''; })
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(String(params[k])); })
      .join('&');
    if (!q) return base;
    return base + (base.indexOf('?') >= 0 ? '&' : '?') + q;
  };
  H.chemicalUrl = function (sbc, id, src) { return H.withParams(sbc, { chemical: id, src: src }); };
  H.searchUrl = function (sbc, q, src) { return H.withParams(sbc, { q: q, src: src }); };
  // The dropdown's rows: every chemical A–Z with nothing typed, narrowing as you type (the tools' own list and ranking)
  H.suggest = function (chems, q) { return PE.browse(chems, q); };
  // Typed text that names exactly one chemical (its name or CAS number), else null
  H.exact = function (chems, q) {
    var t = String(q || '').trim().toLowerCase();
    if (!t) return null;
    var hits = arr(chems).filter(function (c) { return String(c.n || '').toLowerCase() === t || (c.cas && String(c.cas).toLowerCase() === t); });
    return hits.length === 1 ? hits[0] : null;
  };
  // Where a submitted search goes: straight to the chemical when the text names one (or a single suggestion is left),
  // otherwise to Shop by Chemical with the text prefilled (?q=), which also is what the form does without JavaScript
  H.submitUrl = function (sbc, chems, q, src, items) {
    var c = chems ? (H.exact(chems, q) || (arr(items).length === 1 ? items[0] : null)) : null;
    return c ? { url: H.chemicalUrl(sbc, c.id, src), chemical: c } : { url: H.searchUrl(sbc, q, src), chemical: null };
  };

  // ---------------------------------------------------------------- UI
  function el(tag, attrs, children) {
    var e = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === 'class') e.className = attrs[k];
      else if (k.indexOf('on') === 0) e.addEventListener(k.slice(2), attrs[k]);
      else e.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c) e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return e;
  }
  function track(action, value) {
    try { if (window.dataLayer) window.dataLayer.push({ event: 'fmp_hero', fmp_hero_action: action, fmp_hero_value: String(value) }); } catch (e) {}
  }
  function loadScript(url) {
    return new Promise(function (resolve, reject) {
      if (window.PumpEngine) return resolve();
      var s = document.createElement('script');
      s.src = url; s.onload = resolve; s.onerror = function () { reject(new Error('could not load ' + url)); };
      document.head.appendChild(s);
    });
  }

  function mount(root) {
    if (root.__pfh) return;
    root.__pfh = true;
    root.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-pfh-fluid],[data-pfh-chemical]') : null;
      if (!t) return;
      if (t.hasAttribute('data-pfh-fluid')) track('fluid', t.getAttribute('data-pfh-fluid'));
      else track('chemical', t.getAttribute('data-pfh-chemical'));
    });

    var forms = root.querySelectorAll('[data-pfh-search]');
    if (!forms.length) return;
    var FLAME = '<svg class="pfh-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>';

    // One engine and one chemical file for the section, however many boxes it has: the name box and the CAS box share
    // them. Loaded on the first focus of either, never on page load; without them every box still submits as a plain GET.
    var chems = null, loading = null;
    function load(form) {
      if (loading) return loading;
      loading = Promise.all([
        loadScript(form.getAttribute('data-engine')),
        fetch(form.getAttribute('data-chemicals')).then(function (r) { return r.json(); })
      ]).then(function (res) {
        PE = window.PumpEngine; chems = PE.chemicals(res[1]); return chems;
      }).catch(function (err) {
        chems = []; if (window.console) console.error('Pump finder hero: suggestions unavailable', err); return chems;
      });
      return loading;
    }

    Array.prototype.forEach.call(forms, function (form) {
      var input = form.querySelector('input[name="q"]'), list = form.querySelector('[data-pfh-list]');
      if (!input || !list) return;
      var sbc = form.getAttribute('action') || '/pages/shop-by-chemical', src = form.getAttribute('data-src') || '';
      // 'cas' looks up CAS numbers and nothing else, so a word typed there shows nothing rather than the whole A-Z list,
      // and an empty box stays shut instead of dropping two thousand names under a short field.
      var cas = form.getAttribute('data-pfh-mode') === 'cas';
      // Only about a quarter of the chemicals carry a CAS number, so a number we do not hold has to say so. An empty
      // dropdown reads as a broken field.
      var nothing = form.getAttribute('data-pfh-empty') || '';
      var items = [], rows = [], active = -1, listId = list.id, PAGE = 60;

      function suggest(v) { return cas ? PE.browseByCas(chems, v) : H.suggest(chems, v); }
      function close() {
        list.hidden = true; list.innerHTML = ''; items = []; rows = []; active = -1;
        input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant');
      }
      function row(c, i) {
        var fire = PE.flameLabel(c), li = el('li', { role: 'option', id: listId + '-' + i, 'aria-selected': 'false', 'aria-posinset': String(i + 1), 'aria-setsize': String(items.length), onmousedown: function (ev) { ev.preventDefault(); choose(c); } }, [
          el('span', { class: 'pfh-sugg-name' }, [c.n]), c.cas ? el('small', {}, ['CAS ' + c.cas]) : null
        ]);
        if (fire) { var f = el('span', { class: 'pfh-flag' }, [fire]); f.insertAdjacentHTML('afterbegin', FLAME); li.appendChild(f); }
        return li;
      }
      function more() { for (var i = rows.length, end = Math.min(items.length, rows.length + PAGE); i < end; i++) rows.push(list.appendChild(row(items[i], i))); }
      function paint() {
        list.innerHTML = ''; rows = []; active = -1; input.removeAttribute('aria-activedescendant');
        var typed = input.value.trim();
        if (items.length && !typed) list.appendChild(el('li', { class: 'pfh-sugg-head', role: 'presentation' }, [cas ? PE.casNote(items.length) : PE.browseNote(items.length)]));
        var none = !items.length && !!typed && !!nothing;
        if (none) list.appendChild(el('li', { class: 'pfh-sugg-none', role: 'presentation' }, [nothing]));
        more();
        list.scrollTop = 0;
        list.hidden = !items.length && !none;
        input.setAttribute('aria-expanded', String(!list.hidden));
      }
      // moves the highlight, adding rows until it exists, and scrolls the list (not the page) to it
      function setActive(i) {
        if (rows[active]) { rows[active].classList.remove('is-active'); rows[active].setAttribute('aria-selected', 'false'); }
        active = i;
        while (active >= rows.length && rows.length < items.length) more();
        var li = rows[active];
        if (!li) return;
        li.classList.add('is-active'); li.setAttribute('aria-selected', 'true'); input.setAttribute('aria-activedescendant', li.id);
        if (li.offsetTop < list.scrollTop) list.scrollTop = li.offsetTop;
        else if (li.offsetTop + li.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = li.offsetTop + li.offsetHeight - list.clientHeight;
      }
      list.addEventListener('scroll', function () { if (rows.length < items.length && list.scrollTop + list.clientHeight > list.scrollHeight - 200) more(); });
      list.addEventListener('mousedown', function (e) { e.preventDefault(); });  // dragging the list's scrollbar keeps the field focused and the list open
      function update() { if (!chems || document.activeElement !== input) return; items = suggest(input.value); paint(); }
      function choose(c) { track('chemical', c.id); window.location.href = H.chemicalUrl(sbc, c.id, src); }
      function submit() {
        var q = input.value.trim();
        if (!q) { input.focus(); return; }
        var out = H.submitUrl(sbc, chems, q, src, chems ? suggest(q) : []);
        track(out.chemical ? 'chemical' : 'search', out.chemical ? out.chemical.id : q);
        window.location.href = out.url;
      }
      // Focus or a click opens the list; the engine and the chemical file load on the first one. The CAS box has nothing
      // to show until a number is typed, so for it this opens nothing.
      function show() { if (chems) update(); else load(form).then(update); }
      input.addEventListener('focus', show);
      input.addEventListener('click', function () { if (list.hidden) show(); });
      input.addEventListener('input', show);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); if (list.hidden) update(); if (items.length) setActive(Math.min(active + 1, items.length - 1)); }
        else if (e.key === 'ArrowUp' && items.length) { e.preventDefault(); setActive(Math.max(active - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (active >= 0 && items[active]) choose(items[active]); else submit(); }
        else if (e.key === 'Escape') { e.preventDefault(); close(); }  // keeps the text: a search field clears itself on Escape, and the input event that follows reopened the list
      });
      input.addEventListener('blur', function () { setTimeout(function () { if (document.activeElement !== input) close(); }, 120); });  // after a mousedown on a row has run
      form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });
    });
  }

  var PFH = { withParams: H.withParams, chemicalUrl: H.chemicalUrl, searchUrl: H.searchUrl, suggest: H.suggest, exact: H.exact, submitUrl: H.submitUrl, mount: mount };
  if (inNode) module.exports = PFH;
  else if (!window.PFH) {
    window.PFH = PFH;
    var boot = function (scope) { Array.prototype.forEach.call((scope || document).querySelectorAll('[data-pfh]'), mount); };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', function () { boot(); }) : boot();
    // Theme editor: sections are re-rendered in place without a page load
    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  }
})();
